import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Col, Layout, Row, Input } from 'antd';
import { get } from 'lodash';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
import queryString from 'query-string';

import DataTable from 'react-data-table-component';

import PdButton from 'components/pd-button';
import PdCsvDownloader from 'components/pd-csv-downloader';
import { PdCsvUploader } from 'components/pd-csv-uploader';

import CustomerService from 'services/customer';

import { CUSTOMER_CSV_REQUIRED_FIELD } from 'utils';

import './style.scss';

const { Content } = Layout;
const { Search } = Input;

export function Customers() {
  const [customerList, setCustomerList] = useState([]);
  const [loadStatus, setLoadStatus] = useState(true);
  const [nextPage, setNextPage] = useState(1);
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const [sorting] = useState('register_at desc');

  const location = useLocation();
  const parsed = queryString.parse(location.search);
  const navigate = useNavigate();

  function changePage(page) {
    if (page < 1) page = 1;
    parsed['page'] = page;
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/customers',
      search: stringified,
    });
  }

  async function fetchCustomerList(
    page = 1,
    q = '',
    sort_by = sorting,
    total = 10
  ) {
    setLoadStatus(true);
    try {
      const { data } = await CustomerService.getCustomerList(
        page,
        q,
        sort_by,
        total
      );
      const customerData = data.data;
      const metadata = data.metadata;

      setNextPage(metadata.next_cursor);
      setLoadStatus(false);
      setCustomerList(customerData);
    } finally {
      setLoadStatus(false);
    }
  }

  async function handleDownloadCsv() {
    const { data } = await CustomerService.getCustomerReport({
      sort_by: 'email asc',
    });
    const reportData = get(data, 'data', []);
    return reportData
  }

  const buttonAction = (row) => (
    <div className="button-table">
      <Row>
        <Col span={24} className="button">
          <Link to={`/customers/detail?id=${row.id}`}>
            <PdButton type="primary" full style={{ height: 32 }}>
              See Detail
            </PdButton>
          </Link>
        </Col>
      </Row>
    </div>
  );
  const title = (row) => (
    <span className="table-title--data">{`${row.first_name} ${row.last_name}`}</span>
  );

  const columns = [
    {
      name: 'Name',
      cell: title,
      width: '30%',
    },
    {
      name: 'Phone Number',
      selector: row => row.phone_number,
      width: '20%',
    },
    {
      name: 'Email',
      selector: row => row.email,
      width: '18%',
    },
    {
      name: 'Register Date',
      selector: row => row.ordered_at,
      format: (row) => {
        return dayjs(row.register_at).format('DD/MM/YYYY');
      },
      width: '18%',
      center: true,
    },
    {
      name: '',
      cell: buttonAction,
      width: '16%',
    },
  ];

  function searchOnClick(val) {
    if (val === '') {
      delete parsed['q'];
    } else {
      parsed['q'] = val;
    }
    parsed['page'] = 1;
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/customers',
      search: stringified,
    });
  }

  useEffect(() => {
    if (parsed['q']) {
      setSearchValue(parsed['q']);
    } else {
      setSearchValue('');
    }

    if (parsed['page']) {
      if (parsed['page'] < 1) {
        parsed['page'] = 1;
        const stringified = queryString.stringify(parsed);
        navigate({
          pathname: '/customers',
          search: stringified,
        });
      } else {
        setPage(parsed['page']);
      }
    } else {
      setPage('1');
    }

    fetchCustomerList(parsed['page'], parsed['q']);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsed['q'], parsed['page']]);

  return (
    <Layout className="pd-cms-customer main-container">
      <div className="container">
        <div className="content">
          <div className="pageTitle">Customers</div>
          <Content className="pageContent">
            <Row gutter={20}>
              <Col xs={7}>
                <Search
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  enterButton
                  className="search-product"
                  placeholder="Search Customer"
                  onSearch={searchOnClick}
                />
              </Col>
              {customerList.length > 0 && (
                <>
                  <Col xs={{ span: 4, offset: 8 }}>
                    <PdCsvUploader
                      buttonTitle="Bulk Disbursement"
                      requiredFieldName={CUSTOMER_CSV_REQUIRED_FIELD}
                      purpose="customer"
                    />
                  </Col>

                  <Col xs={4}>
                    <PdCsvDownloader
                    buttonLabel='Download'
                      datas={handleDownloadCsv}
                      filename="customer-data-homestory"
                    />
                  </Col>
                </>
              )}

              {!loadStatus && (
                <>
                  <Col xs={24}>
                    <DataTable
                      columns={columns}
                      data={customerList}
                      progressPending={loadStatus}
                      noHeader
                      highlightOnHover
                      responsive={false}
                      striped
                    />
                  </Col>

                  <Col span={8} offset={8}>
                    <div className="center-button">
                      {page !== '1' && (
                        <PdButton
                          type="primary"
                          outline
                          style={{ height: 32 }}
                          onClick={() => changePage(Number(page) - 1)}>
                          Previous
                        </PdButton>
                      )}
                      {nextPage !== -1 && (
                        <PdButton
                          type="primary"
                          outline
                          style={{ height: 32 }}
                          onClick={() => changePage(Number(page) + 1)}>
                          Next
                        </PdButton>
                      )}
                    </div>
                  </Col>
                </>
              )}
            </Row>
          </Content>
        </div>
      </div>
    </Layout>
  );
}

export default { Customers };
